// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGaugeConstants.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import constants from 'logic/data/Izenda.ReportPartFieldConstants';
import MapCollection from 'logic/data/Izenda.MapCollectionConstants';
import localizer from 'share/localization/Izenda.Localizer';

const FUNCTION = constants.FUNCTION;
const FORMAT_KEY = constants.FORMAT_KEY;

const MAP_TYPES = {
  World: 'World',
  Continent: 'Continent',
  Country: 'Country'
};

const CHART_TYPES = {
  get World() {
    return localizer.getText('REPORT_PART_MAP_CONSTANTS_WORLD');
  },
  get Continent() {
    return localizer.getText('REPORT_PART_MAP_CONSTANTS_CONTINENT');
  },
  get Country() {
    return localizer.getText('REPORT_PART_MAP_CONSTANTS_COUNTRY');
  }
};

const ContainerName = {
  // Point Options

  POINT_OPTIONS: 'pointOptions', // save to back-end
  COUNTRY: 'country',
  STATE: 'state',
  COUNTY: 'county',
  CITY: 'city',
  POSTAL_CODE: 'postalCode',
  LATITUDE: 'latitude',
  LONGTITUDE: 'longtitude',

  // Values
  SHADING_VALUES: 'shadingMetric',
  BUBBLE_VALUES: 'bubbleMetrics'
};

const POINT_OPTIONS_CONTAINERS = [
  ContainerName.COUNTRY,
  ContainerName.STATE,
  ContainerName.COUNTY,
  ContainerName.CITY,
  ContainerName.POSTAL_CODE,
  ContainerName.LATITUDE,
  ContainerName.LONGTITUDE
];
const VALUES_CONTAINERS = [ContainerName.SHADING_VALUES, ContainerName.BUBBLE_VALUES];

/* MAPPING_KEY */
const MappingKey = {};
MappingKey[ContainerName.COUNTRY] = ['hc-key', 'hc-a2', 'name', 'country-abbrev', 'iso-a3', 'iso-a2'];
MappingKey[ContainerName.STATE] = ['hc-key', 'hc-a2', 'state-fips', 'flips', 'name', 'woeName', 'hcA2'];
MappingKey[ContainerName.COUNTY] = ['hc-key', 'hc-a2', 'state-fips', 'fips', 'name'];
MappingKey[ContainerName.CITY] = ['geonameId', 'name', 'asciiName', 'alternateNames'];
MappingKey[ContainerName.POSTAL_CODE] = ['postalCode', 'postalName'];

/* Default function for each fields based on its container */
const DefaultFunction = {};

DefaultFunction[ContainerName.COUNTRY] = DefaultFunction[ContainerName.STATE] = DefaultFunction[
  ContainerName.COUNTY
] = DefaultFunction[ContainerName.CITY] = DefaultFunction[ContainerName.POSTAL_CODE] = DefaultFunction[
  ContainerName.LATITUDE
] = DefaultFunction[ContainerName.LONGTITUDE] = DefaultFunction[ContainerName.COUNTRY] = {
  Datetime: {
    FUNCTION: FUNCTION.GROUP,
    FORMAT: FORMAT_KEY.YEAR
  },
  Time: {
    FUNCTION: FUNCTION.GROUP,
    FORMAT: FORMAT_KEY.DEFAULT_TIME
  },

  Others: {
    FUNCTION: FUNCTION.GROUP,
    FORMAT: FORMAT_KEY.NONE
  }
};

DefaultFunction[ContainerName.SHADING_VALUES] = DefaultFunction[ContainerName.BUBBLE_VALUES] = {
  Numeric: {
    FUNCTION: FUNCTION.SUM,
    FORMAT: FORMAT_KEY.NONE
  },

  Money: {
    FUNCTION: FUNCTION.SUM,
    FORMAT: FORMAT_KEY.NONE
  },

  Others: {
    FUNCTION: FUNCTION.COUNT,
    FORMAT: FORMAT_KEY.NONE
  }
};

/* Maximum number of fields for each container */
const MaximumField = {};

MaximumField[ContainerName.COUNTRY] = MaximumField[ContainerName.STATE] = MaximumField[
  ContainerName.COUNTY
] = MaximumField[ContainerName.CITY] = MaximumField[ContainerName.POSTAL_CODE] = MaximumField[
  ContainerName.LATITUDE
] = MaximumField[ContainerName.LONGTITUDE] = MaximumField[ContainerName.COUNTRY] = MaximumField[
  ContainerName.SHADING_VALUES
] = 1;

/* Messages */
const Message = {};

/* Other rules */
const POINTS_OPTIONS_ORDER = [
  ContainerName.COUNTRY,
  ContainerName.STATE,
  ContainerName.COUNTY,
  ContainerName.CITY,
  ContainerName.POSTAL_CODE,
  ContainerName.LATITUDE,
  ContainerName.LONGTITUDE
];

const POINTS_OPTIONS_FOR_SHADING = [ContainerName.COUNTRY, ContainerName.STATE, ContainerName.COUNTY];
const POINTS_OPTIONS_FOR_BUBBLE_ALL = [
  ContainerName.COUNTRY,
  ContainerName.STATE,
  ContainerName.COUNTY,
  ContainerName.CITY,
  ContainerName.POSTAL_CODE,
  ContainerName.LATITUDE,
  ContainerName.LONGTITUDE
];
const POINTS_OPTIONS_FOR_BUBBLE_WITHOUT_LONGLAT = [
  ContainerName.COUNTRY,
  ContainerName.STATE,
  ContainerName.COUNTY,
  ContainerName.CITY,
  ContainerName.POSTAL_CODE
];

//anytime when those values change >> drilldownInfo need to reset
const PROPS_APPLY_NEW_DRILLDOWN = ['chartType', 'continent', 'country', 'state'];

const DRILLDOWN_LEVELS = {
  WORLD: 'WORLD',
  CONTINENT: 'CONTINENT',
  COUNTRY: 'COUNTRY',
  STATE: 'STATE'
};

const DRILLDOWN_ORDERS = [
  DRILLDOWN_LEVELS.WORLD,
  DRILLDOWN_LEVELS.CONTINENT,
  DRILLDOWN_LEVELS.COUNTRY,
  DRILLDOWN_LEVELS.STATE
];

/* Map Constant UI */
const MapUIConstant = {
  backgroundMapColor: '#f2f2f2',
  hoverColor: '#BADA55',
  bubbleMinSize: 4,
  bubbleMaxSize: '10%'
};

/* Key to get value for map label */
const DATA_LABELS = {
  WORLD: 'hc-a2',
  CONTINENT: 'hc-a2',
  COUNTRY: 'postal-code',
  STATE: 'name'
};

const FUNCTION_CONFIG = {
  POINT_OPTIONS: {
    SUPPORTED: {
      Datetime: [FUNCTION.GROUP, FUNCTION.GROUP_DAYS_OLD],
      DEFAULT: [FUNCTION.GROUP]
    }
  },

  VALUES: {
    UNSUPPORTED: {
      DEFAULT: [FUNCTION.NONE, FUNCTION.GROUP],
      Datetime: [FUNCTION.NONE, FUNCTION.GROUP, FUNCTION.MAXIMUM, FUNCTION.MINIMUM]
    }
  }
};

const DEFAULT_SETTING_VALUE = {
  borderDefault: {
    'izEntire.borderColor': '#000',
    'izEntire.borderWidth': undefined,
    'izEntire.borderStyle': 'solid'
  },
  borderNew: {
    'izEntire.borderColor': undefined,
    'izEntire.borderWidth': undefined,
    'izEntire.borderStyle': undefined
  },
  backGroundColorDefault: {
    'izEntire.backgroundColor': '#fff',
    'izPlot.backgroundColor': undefined
  },
  backGroundColorNew: {
    'izEntire.backgroundColor': undefined,
    'izPlot.backgroundColor': undefined
  },
  refreshIntervalDefault: {
    enable: false,
    isAll: true,
    latestRecord: 0,
    updateInterval: 0
  }
};

const PROVISION_DATA_NOTIFICATION_MESSAGE_KEY = {
  PROVISIONING: 'ProvisionImporting',
  SUCCESS: 'ProvisionSuccess',
  ERROR: 'ProvisionError'
};

export default {
  CHART_TYPES,
  MAP_TYPES,
  ContainerName,

  DefaultChartType: MAP_TYPES.World,
  DefaultGEOBackground: 'world',
  DefaultContinent: 'central-america',
  DefaultCountry: 'al',

  DefaultFunction,
  MaximumField,

  MappingKey,
  MapUIConstant,

  PointOptionsContainers: POINT_OPTIONS_CONTAINERS,
  ValuesContainers: VALUES_CONTAINERS,

  PointsOptionsOrder: POINTS_OPTIONS_ORDER,
  PointsOptionsForShading: POINTS_OPTIONS_FOR_SHADING,
  PointsOptionsForBubbleAll: POINTS_OPTIONS_FOR_BUBBLE_ALL,
  PointsOptionsForBubbleWithoutLongLat: POINTS_OPTIONS_FOR_BUBBLE_WITHOUT_LONGLAT,
  PropsApplyNewDrilldown: PROPS_APPLY_NEW_DRILLDOWN,
  DrilldownLevels: DRILLDOWN_LEVELS,
  DrilldownOrders: DRILLDOWN_ORDERS,
  DataLabels: DATA_LABELS,

  get KeyNames() {
    return {
      [ContainerName.COUNTRY]: localizer.getText('REPORT_PART_MAP_POINT_country'),
      [ContainerName.STATE]: localizer.getText('REPORT_PART_MAP_POINT_state'),
      [ContainerName.COUNTY]: localizer.getText('REPORT_PART_MAP_POINT_county'),
      [ContainerName.CITY]: localizer.getText('REPORT_PART_MAP_POINT_city'),
      [ContainerName.POSTAL_CODE]: localizer.getText('REPORT_PART_MAP_POINT_postalCode'),
      [ContainerName.LATITUDE]: localizer.getText('REPORT_PART_MAP_POINT_latitude'),
      [ContainerName.LONGTITUDE]: localizer.getText('REPORT_PART_MAP_POINT_longtitude'),
      [ContainerName.SHADING_VALUES]: localizer.getText('REPORT_PART_MAP_CONSTANTS_SHADING_METRIC'),
      [ContainerName.BUBBLE_VALUES]: localizer.getText('REPORT_PART_MAP_CONSTANTS_BUBBLE_METRIC')
    };
  },
  FunctionConfig: FUNCTION_CONFIG,
  DEFAULT_SETTING_VALUE,
  PROVISION_DATA_NOTIFICATION_MESSAGE_KEY
};



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.ReportPartMapConstants.js